import React, { useCallback } from 'react';

import { useApolloClient } from '@apollo/client/react';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';

import { SkillAccessMenu } from './skill-access-menu';
import type { EditableSkillScope } from './skill-access-menu';
import { getIsOutdatedSkillVersionError } from './skill-save-error';

export const SkillAccessCell = React.memo(function SkillAccessCell({
  row,
}: GridRenderCellParams<Apollo.AgentSkillListItemFragment>): JSX.Element {
  const currentUser = useSelectCurrentUser();
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const [setSkillScope] = Apollo.useSetAgentSkillScopeMutation();
  const canEdit =
    row.owner.id === currentUser?.id && row.scope !== Apollo.AgentSkillScope.Community;

  const changeScope = useCallback(
    async (scope: EditableSkillScope) => {
      if (!canEdit || scope === row.scope) return;
      try {
        await setSkillScope({
          optimisticResponse: {
            __typename: 'Mutation',
            setAgentSkillScope: {
              __typename: 'AgentSkillResponse',
              agentSkill: { ...row, __typename: 'AgentSkill', scope },
            },
          },
          variables: {
            input: {
              expectedVersionId: row.currentVersion?.id ?? null,
              id: row.id,
              scope,
            },
          },
        });
      } catch (error) {
        const outdated = getIsOutdatedSkillVersionError(error);
        if (outdated) void client.refetchQueries({ include: [Apollo.GetAgentSkillsDocument] });
        enqueueSnackbar(
          outdated
            ? 'This skill changed since the list loaded. Try again.'
            : 'Could not change skill access. Please try again.',
          { variant: 'error' }
        );
      }
    },
    [canEdit, client, enqueueSnackbar, row, setSkillScope]
  );
  const stopPropagation = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), []);

  return (
    <div
      style={{ alignItems: 'center', display: 'flex', height: '100%' }}
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <SkillAccessMenu canEdit={canEdit} scope={row.scope} onChange={changeScope} />
    </div>
  );
});
